/**
 * cxCheckLine — the ONE definition of the entry guard's arithmetic (design §3).
 *
 * THE BROWSER COPY of backend/cx/check/cxCheckLine.js — identical body, ES-module exports.
 * The popup's live verdict and the server's must never disagree, so
 * frontend/src/utils/cxCheckLine.test.js runs a case table through BOTH and fails on any
 * drift. Change one, change the other (or regenerate this file from the backend one).
 *
 *   §3.1 unit family   weight kg/g · volume L/ml · count pcs (a stray dozen = 12 pcs)
 *   §3.2 normalise     base_qty = qty × factor; base_price = total ÷ base_qty (g / ml / piece)
 *   §3.3 window        M = median of the last 5 trusted base prices; w = clamp(3·MAD/M, .30, .60)
 *                      green M/(1+w)…M(1+w) · amber M/(1+2w)…M(1+2w) · red beyond
 *                      (ratio-symmetric — the design's M(1−2w) floor let a too-low price
 *                      escape red whenever w ≥ 0.5; change map §2.2)
 *   §3.4 slip rule     r = base_price ÷ M within ±30% of 0.001, 0.01, 0.1, 10, 100, 1000 → red;
 *                      ×1000 on weight/volume = unit slip (swap g↔kg, ml↔L), else decimal slip
 *   §3.5 quantity      ≤5× the largest of the last 5 → green · ≤10× amber · >10× red
 *   §3.6 final status  the worse of price and quantity; ₹0 → red "use complimentary"
 */

const W_MIN = 0.30;
const W_MAX = 0.60;
const W_RARE = 0.60;
const SLIP_POWERS = [0.001, 0.01, 0.1, 10, 100, 1000];
const SLIP_TOLERANCE = 0.30;
const QTY_AMBER = 5;
const QTY_RED = 10;
const MAX_FIXES = 2;

const UNIT_ALIASES = {
  kg: 'kg', kgs: 'kg', kilo: 'kg', kilos: 'kg', kilogram: 'kg', kilograms: 'kg',
  g: 'g', gm: 'g', gms: 'g', gr: 'g', gram: 'g', grams: 'g',
  l: 'L', ltr: 'L', ltrs: 'L', lt: 'L', litre: 'L', litres: 'L', liter: 'L', liters: 'L',
  ml: 'ml', mls: 'ml', millilitre: 'ml', millilitres: 'ml', milliliter: 'ml', milliliters: 'ml',
  pcs: 'pcs', pc: 'pcs', piece: 'pcs', pieces: 'pcs', nos: 'pcs',
  dozen: 'dozen', doz: 'dozen', dz: 'dozen',
};

const UNITS = {
  kg: { family: 'weight', factor: 1000 },
  g: { family: 'weight', factor: 1 },
  L: { family: 'volume', factor: 1000 },
  ml: { family: 'volume', factor: 1 },
  pcs: { family: 'count', factor: 1 },
  dozen: { family: 'count', factor: 12 },
};

/** Display unit per family: prices are always shown per kg / per L / per piece. */
const FAMILY_DISPLAY = {
  weight: { unit: 'kg', factor: 1000, big: 'kg', small: 'g', words: 'kg or g' },
  volume: { unit: 'L', factor: 1000, big: 'L', small: 'ml', words: 'L or ml' },
  count: { unit: 'piece', factor: 1, big: 'pcs', small: 'pcs', words: 'pieces' },
};

const ZONE_RANK = { green: 0, amber: 1, red: 2 };
const FIX_PRIORITY = { unit: 0, price: 1, quantity: 2, quantity_and_total: 3 };

function round(value, dp) {
  const n = Number(value);
  if (!Number.isFinite(n)) return NaN;
  const sign = n < 0 ? -1 : 1;
  const abs = Math.abs(n);
  const text = String(abs);
  const shifted = text.indexOf('e') !== -1 ? Math.round(abs * Math.pow(10, dp)) : Math.round(Number(text + 'e' + dp));
  return (sign * Number(shifted + 'e-' + dp)) || 0;
}

function unitInfo(unit) {
  const key = String(unit == null ? '' : unit).trim().toLowerCase();
  const canon = Object.prototype.hasOwnProperty.call(UNIT_ALIASES, key) ? UNIT_ALIASES[key] : null;
  if (!canon) return null;
  return { unit: canon, family: UNITS[canon].family, factor: UNITS[canon].factor };
}

function median(values) {
  const v = values.filter((x) => Number.isFinite(x)).slice().sort((a, b) => a - b);
  if (v.length === 0) return null;
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
}

/** M, MAD and w from a list of base prices (the window builder and the tests use this). */
function windowStats(prices, rare) {
  const ps = (prices || []).map(Number).filter((p) => p > 0);
  if (ps.length === 0) return null;
  const m = median(ps);
  const mad = median(ps.map((p) => Math.abs(p - m)));
  const d = m > 0 ? mad / m : 0;
  const w = rare ? W_RARE : Math.min(W_MAX, Math.max(W_MIN, 3 * d));
  return { median: m, mad: mad, d: d, w: w };
}

function bands(m, w) {
  return { greenLow: m / (1 + w), greenHigh: m * (1 + w), amberLow: m / (1 + 2 * w), amberHigh: m * (1 + 2 * w) };
}

/** §3.4 — the power of ten r sits within ±30% of, if any. */
function nearestSlip(ratio) {
  if (!(ratio > 0)) return null;
  let best = null;
  for (let i = 0; i < SLIP_POWERS.length; i++) {
    const p = SLIP_POWERS[i];
    const dist = Math.abs(Math.log(ratio / p));
    if (best === null || dist < best.dist) best = { power: p, dist: dist };
  }
  return best && Math.abs(ratio / best.power - 1) <= SLIP_TOLERANCE ? { power: best.power } : null;
}

function judgePrice(basePrice, win) {
  const m = Number(win.median);
  const b = bands(m, Number(win.w));
  const ratio = basePrice / m;
  const slip = nearestSlip(ratio);
  let zone = 'red';
  if (slip) zone = 'red';
  else if (basePrice >= b.greenLow && basePrice <= b.greenHigh) zone = 'green';
  else if (basePrice >= b.amberLow && basePrice <= b.amberHigh) zone = 'amber';
  return { zone: zone, ratio: ratio, slip: slip, bands: b };
}

function judgeQuantity(baseQty, win) {
  const max = Number(win.qtyMax);
  if (!(max > 0)) return { zone: 'green', ratio: null };
  const ratio = baseQty / max;
  return { zone: ratio <= QTY_AMBER ? 'green' : ratio <= QTY_RED ? 'amber' : 'red', ratio: ratio };
}

function worst(a, b) {
  return ZONE_RANK[a] >= ZONE_RANK[b] ? a : b;
}

function isActive(win) {
  return Boolean(win) && (win.status === 'protected' || win.status === 'provisional')
    && Number(win.median) > 0 && Number(win.w) > 0;
}

/**
 * The quantity and unit the base price is measured in. A row bought in packs carries its
 * pack size (supplier conversion: 1 pack = 500 g): its base quantity is packs × 500 g.
 */
function effectiveUnit(line) {
  const u = unitInfo(line.unit);
  const pack = line.pack;
  if (pack && Number(pack.quantity) > 0) {
    const pu = unitInfo(pack.unit);
    if (!pu) return null;
    return { unit: pu.unit, family: pu.family, factor: pu.factor * Number(pack.quantity), packed: true };
  }
  return u ? { unit: u.unit, family: u.family, factor: u.factor, packed: false } : null;
}

/**
 * Judge one stock-in line.
 *
 * @param {{ quantity:number, unit:string, total:number, pack?:{quantity:number, unit:string} }} line
 *        quantity and total exactly as the submit sends them (qty + free qty, gross total)
 * @param {{ item?:{ family?:string, name?:string },
 *           window?:{ status:string, median:number, w:number, qtyMax?:number, qtyMedian?:number } }} ctx
 * @param {{ fixes?: boolean }} [opts]
 * @returns {{ status:'green'|'amber'|'red'|'learning'|'skip', reasons:object[], family:object|null,
 *             base:object|null, price:object|null, quantity:object|null, fixes:object[] }}
 */
function cxCheckLine(line, ctx, opts) {
  const item = (ctx && ctx.item) || {};
  const win = ctx && ctx.window;
  const out = { status: 'green', reasons: [], family: null, base: null, price: null, quantity: null, fixes: [] };
  const q = Number(line.quantity);
  const t = Number(line.total);

  if (!(q > 0)) {
    out.status = 'red';
    out.reasons.push({ code: 'BAD_QUANTITY', message: 'Enter a quantity above 0' });
    return out;
  }
  const eu = effectiveUnit(line);
  if (!eu) {
    out.status = 'skip';
    out.reasons.push({ code: 'UNKNOWN_UNIT', message: 'Unit not recognised' });
    return out;
  }
  const expected = item.family || null;
  out.family = { expected: expected, entered: eu.family, ok: !expected || expected === eu.family };
  if (!out.family.ok) {
    out.status = 'red';
    const words = FAMILY_DISPLAY[expected] ? FAMILY_DISPLAY[expected].words : expected;
    out.reasons.push({ code: 'WRONG_UNIT_FAMILY', message: (item.name || 'This item') + ' is measured in ' + words });
    return out;
  }
  if (!(t > 0)) {
    out.status = 'red';
    out.reasons.push({ code: 'ZERO_PRICE', message: 'Is this free? Use complimentary instead' });
    return out;
  }

  const baseQty = q * eu.factor;
  const basePrice = t / baseQty;
  out.base = { qty: baseQty, price: basePrice, unit: eu.unit, family: eu.family };
  if (!isActive(win)) {
    out.status = 'learning';
    return out;
  }

  out.price = judgePrice(basePrice, win);
  out.quantity = judgeQuantity(baseQty, win);
  out.status = worst(out.price.zone, out.quantity.zone);
  if (out.price.slip) {
    const unitSlip = (out.price.slip.power === 1000 || out.price.slip.power === 0.001) && eu.family !== 'count' && !eu.packed;
    out.reasons.push({ code: unitSlip ? 'UNIT_SLIP' : 'DECIMAL_SLIP', power: out.price.slip.power });
  } else if (out.price.zone !== 'green') {
    out.reasons.push({ code: out.price.zone === 'red' ? 'PRICE_FAR_OFF' : 'PRICE_CHANGED', ratio: out.price.ratio });
  }
  if (out.quantity.zone !== 'green') {
    out.reasons.push({ code: out.quantity.zone === 'red' ? 'QUANTITY_SLIP' : 'BIG_ORDER', ratio: out.quantity.ratio });
  }
  if (out.status === 'red' && !(opts && opts.fixes === false)) {
    out.fixes = suggestFixes(line, eu, ctx, out);
  }
  return out;
}

/** §3.4 step 4 — candidate fixes, each re-checked; only those that land green, best first, at most two. */
function suggestFixes(line, eu, ctx, verdict) {
  const win = ctx.window;
  const q = Number(line.quantity);
  const t = Number(line.total);
  const fam = FAMILY_DISPLAY[eu.family];
  const cands = [];
  const add = (kind, quantity, unit, total) => {
    cands.push({ kind: kind, quantity: round(quantity, 3), unit: unit, total: round(total, 2) });
  };
  const rowUnit = unitInfo(line.unit);
  const slip = verdict.price && verdict.price.slip;
  if (slip) {
    const p = slip.power;
    if ((p === 1000 || p === 0.001) && !eu.packed && eu.family !== 'count' && rowUnit) {
      if (p === 1000 && rowUnit.unit === fam.small) add('unit', q, fam.big, t);
      if (p === 0.001 && rowUnit.unit === fam.big) add('unit', q, fam.small, t);
    }
    add('price', q, line.unit, t / p);
    add('quantity', q * p, line.unit, t);
  }
  if (verdict.quantity && verdict.quantity.zone === 'red') {
    for (const k of [10, 100, 1000]) {
      add('quantity_and_total', q / k, line.unit, t / k);
      add('quantity', q / k, line.unit, t);
    }
  }

  const seen = {};
  const kept = [];
  for (let i = 0; i < cands.length; i++) {
    const c = cands[i];
    if (!(c.quantity > 0) || !(c.total > 0)) continue;
    const r = cxCheckLine({ quantity: c.quantity, unit: c.unit, total: c.total, pack: line.pack }, ctx, { fixes: false });
    if (r.status !== 'green' || !r.base) continue;
    const key = round(r.base.qty, 6) + '|' + c.total;
    if (seen[key]) continue;
    seen[key] = true;
    kept.push({
      kind: c.kind, quantity: c.quantity, unit: c.unit, total: c.total,
      basePrice: r.base.price,
      _priceDist: round(Math.abs(Math.log(r.base.price / Number(win.median))), 9),
      _qtyDist: Number(win.qtyMedian) > 0 ? Math.abs(Math.log(r.base.qty / Number(win.qtyMedian))) : 0,
    });
  }
  kept.sort((a, b) => (a._priceDist - b._priceDist)
    || (FIX_PRIORITY[a.kind] - FIX_PRIORITY[b.kind])
    || (a._qtyDist - b._qtyDist));
  return kept.slice(0, MAX_FIXES).map((f) => ({ kind: f.kind, quantity: f.quantity, unit: f.unit, total: f.total, basePrice: f.basePrice }));
}

/** Price per display unit (₹/kg, ₹/L, ₹/piece) for a base price of a family. */
function displayPrice(basePrice, family) {
  const d = FAMILY_DISPLAY[family];
  return d ? Number(basePrice) * d.factor : Number(basePrice);
}

export {
  cxCheckLine,
  unitInfo,
  windowStats,
  bands,
  nearestSlip,
  median,
  displayPrice,
  round,
  W_MIN,
  W_MAX,
  W_RARE,
  SLIP_POWERS,
  SLIP_TOLERANCE,
  QTY_AMBER,
  QTY_RED,
  FAMILY_DISPLAY,
};
